import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Icon,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "../../../axios";
import usePageTitle from "../../pageTitle";
import AppConfig from "../../../appConfig";

function LoyaltyPromotionClaimPage() {
  usePageTitle("Submit Purchase");

  const { ref } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const productImageInput = useRef(null);
  const purchaseEvidenceInput = useRef(null);

  const cameraVideoRef = useRef(null);
  const cameraCanvasRef = useRef(null);
  const cameraStreamRef = useRef(null);

  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraTarget, setCameraTarget] = useState("product");
  const [cameraError, setCameraError] = useState("");

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [registering, setRegistering] = useState(false);

  const [promotion, setPromotion] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");

  const [token, setToken] = useState(searchParams.get("token") || "");

  const [productImage, setProductImage] = useState(null);
  const [purchaseEvidence, setPurchaseEvidence] = useState(null);

  const [productImagePreview, setProductImagePreview] = useState("");
  const [purchaseEvidencePreview, setPurchaseEvidencePreview] = useState("");

  const [submitted, setSubmitted] = useState(false);
  const [purchaseReference, setPurchaseReference] = useState("");

  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [gender, setGender] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  const showMessage = (message, severity = "error") => {
    setSnackbarMessage(message || "Unable to complete request.");
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    setToken(searchParams.get("token") || "");
  }, [searchParams]);

  useEffect(() => {
    const fetchPromotion = async () => {
      setLoading(true);

      try {
        const response = await axios.get(
          `web/tm/loyalty/promotions/${ref}/fetch.json`,
        );

        if (response.data.status !== "success") {
          showMessage(response.data.data?.message || "Promotion not found.");
          return;
        }

        const data = response.data.data;

        setPromotion(data.promotion || null);
        setProducts(data.products || []);
      } catch (error) {
        showMessage(
          error.response?.data?.data?.message ||
            "Unable to load this promotion.",
        );
      } finally {
        setLoading(false);
      }
    };

    if (ref) fetchPromotion();
  }, [ref]);

  useEffect(() => {
    return () => {
      if (productImagePreview) {
        URL.revokeObjectURL(productImagePreview);
      }

      if (purchaseEvidencePreview) {
        URL.revokeObjectURL(purchaseEvidencePreview);
      }
    };
  }, [productImagePreview, purchaseEvidencePreview]);

  const getImageUrl = (image) => {
    if (!image) return "";

    // if (/^https?:\/\//i.test(image)) return image;

    // return image.startsWith("/") ? image : `/media/${image}`;
    return `${AppConfig.api_media_url}${image}`;
  };

  const handleProductImage = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showMessage("Please select a valid product image.");
      return;
    }

    if (productImagePreview) {
      URL.revokeObjectURL(productImagePreview);
    }

    setProductImage(file);
    setProductImagePreview(URL.createObjectURL(file));
  };

  const handlePurchaseEvidence = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      showMessage("Purchase evidence must be an image or PDF.");
      return;
    }

    if (purchaseEvidencePreview) {
      URL.revokeObjectURL(purchaseEvidencePreview);
    }

    setPurchaseEvidence(file);
    setPurchaseEvidencePreview(
      file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
    );
  };

  const openCamera = (target) => {
    setCameraTarget(target);
    setCameraError("");
    setCameraOpen(true);
  };

  const closeCamera = () => {
    setCameraOpen(false);

    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }

    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    if (!cameraOpen) return undefined;

    let cancelled = false;

    const startCamera = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setCameraError(
            "Camera access is not supported by this browser. Please use Browse files instead.",
          );
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        cameraStreamRef.current = stream;

        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
          await cameraVideoRef.current.play();
        }
      } catch (error) {
        setCameraError(
          error.name === "NotAllowedError"
            ? "Camera permission was denied. Allow camera access in your browser settings, or use Browse files instead."
            : "Unable to access the camera. Check that your device has a camera and that no other app is using it.",
        );
      }
    };

    startCamera();

    return () => {
      cancelled = true;

      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
    };
  }, [cameraOpen]);

  const captureCameraPhoto = () => {
    const video = cameraVideoRef.current;
    const canvas = cameraCanvasRef.current;

    if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
      setCameraError("The camera is not ready yet. Please try again.");
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext("2d");

    if (!context) {
      setCameraError("Unable to capture the photo. Please try again.");
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setCameraError("Unable to capture the photo. Please try again.");
          return;
        }

        const file = new File(
          [blob],
          `${cameraTarget}-photo-${Date.now()}.jpg`,
          {
            type: "image/jpeg",
            lastModified: Date.now(),
          },
        );

        if (cameraTarget === "product") {
          handleProductImage(file);
        } else {
          handlePurchaseEvidence(file);
        }

        closeCamera();
      },
      "image/jpeg",
      0.92,
    );
  };

  const resetRegistration = () => {
    setFirstName("");
    setLastName("");
    setEmailAddress("");
    setPhoneNumber("");
    setGender("");
  };

  const registerConsumer = async () => {
    if (!promotion?.reference) {
      showMessage("Promotion information is unavailable.");
      return;
    }

    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !emailAddress.trim() ||
      !phoneNumber.trim() ||
      !gender
    ) {
      showMessage("Please complete all the required fields.");
      return;
    }

    setRegistering(true);

    try {
      const response = await axios.post(
        "web/tm/loyalty/promotions/register.json",
        {
          reference: promotion.reference,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email_address: emailAddress.trim(),
          phone_number: phoneNumber.trim(),
          gender,
        },
      );

      if (response.data.status !== "success") {
        showMessage(
          response.data.data?.message || "Unable to complete registration.",
        );
        return;
      }

      const consumer = response.data.data?.consumer;

      if (!consumer?.token) {
        showMessage("Registration completed, but no unique code was returned.");
        return;
      }

      setToken(consumer.token);
      setRegistrationOpen(false);
      resetRegistration();

      showMessage(
        response.data.data.message ||
          "Registration successful. Your unique code has been added.",
        "success",
      );
    } catch (error) {
      showMessage(
        error.response?.data?.data?.message ||
          "Unable to complete registration. Please try again.",
      );
    } finally {
      setRegistering(false);
    }
  };

  const submitClaim = async () => {
    if (!ref) {
      showMessage("Promotion reference is missing.");
      return;
    }

    if (!token.trim()) {
      showMessage("Please enter your loyalty code.");
      return;
    }

    if (!selectedProduct) {
      showMessage("Please select the product you purchased.");
      return;
    }

    if (!productImage) {
      showMessage("Please upload a photo of the product.");
      return;
    }

    if (!purchaseEvidence) {
      showMessage("Please upload your receipt or purchase evidence.");
      return;
    }

    const formData = new FormData();

    formData.append("reference", ref);
    formData.append("product", selectedProduct);
    formData.append("token", token.trim());
    formData.append("product_image", productImage);
    formData.append("purchase_evidence", purchaseEvidence);

    setSubmitting(true);
    setLoading(true);

    try {
      const response = await axios.post(
        "web/tm/loyalty/promotions/claim.json",
        formData,
      );
      console.log(response.data);

      if (response.data.status !== "success") {
        showMessage(
          response.data.data?.message || "Unable to submit your purchase.",
        );
        return;
      }

      setPurchaseReference(response.data.data.purchase?.reference || "");
      setSubmitted(true);
      setLoading(true);

      showMessage(
        response.data.data.message ||
          "Your purchase has been submitted successfully.",
        "success",
      );
    } catch (error) {
      showMessage(
        error.response?.data?.data?.message ||
          "Unable to submit your purchase. Please try again.",
      );
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const cardSx = {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    boxShadow: "0 2px 12px rgba(0,0,0,0.035)",
    bgcolor: "background.paper",
  };

  const uploadBoxSx = {
    border: "1px dashed",
    borderColor: "divider",
    borderRadius: 1.5,
    p: 2,
    minHeight: 145,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    bgcolor: "#fafafa",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f6f8",
        color: "#17191d",
        pb: { xs: 4, md: 7 },
      }}
    >
      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: "rgba(18,20,24,0.45)",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box
        sx={{
          height: 8,
          background: "linear-gradient(90deg, #17191d 0%, #575b64 100%)",
        }}
      />

      <Box
        sx={{
          background:
            "radial-gradient(ellipse at 90% 0%, rgba(255,255,255,0.10), transparent 38%), linear-gradient(125deg, #17191d 0%, #292d35 100%)",
          color: "#fff",
          px: { xs: 2.5, md: 5 },
          pt: { xs: 4, md: 6 },
          pb: { xs: 5, md: 7 },
        }}
      >
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ mb: 4 }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: "#fff",
                color: "#17191d",
                fontWeight: 800,
                fontSize: 18,
              }}
            >
              <Icon>loyalty</Icon>
            </Box>

            <Typography
              variant="subtitle1"
              sx={{ color: "#fff", fontWeight: 700, letterSpacing: 0.2 }}
            >
              Consumer Loyalty Program
            </Typography>
          </Stack>

          <Typography
            variant="overline"
            sx={{
              color: "rgba(255,255,255,0.65)",
              letterSpacing: 2,
              fontWeight: 600,
            }}
          >
            {promotion?.title || "Loyalty promotion"}
          </Typography>

          <Typography
            variant="h3"
            sx={{
              mt: 1,
              maxWidth: 760,
              fontWeight: 700,
              letterSpacing: { xs: -0.7, md: -1.4 },
              lineHeight: 1.15,
              fontSize: { xs: "2rem", sm: "2.6rem", md: "3rem" },
              overflowWrap: "anywhere",
            }}
          >
            Submit your purchase.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mt: 2,
              maxWidth: 650,
              lineHeight: 1.8,
              color: "rgba(255,255,255,0.72)",
              whiteSpace: "pre-line",
            }}
          >
            {promotion?.description ||
              "Select the product you purchased, enter your unique code, and upload your product photo and purchase evidence."}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 3, flexWrap: "wrap", rowGap: 1 }}
          >
            <Chip
              size="small"
              label="PURCHASE CLAIM"
              sx={{
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            />

            <Chip
              size="small"
              label="Product and receipt required"
              sx={{
                color: "rgba(255,255,255,0.85)",
                bgcolor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.16)",
              }}
            />
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          px: { xs: 2, md: 4 },
          mt: { xs: -2.5, md: -3 },
        }}
      >
        {submitted ? (
          <Card
            sx={{
              ...cardSx,
              p: { xs: 3, md: 4 },
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: "#e5f5eb",
                color: "#267446",
                display: "grid",
                placeItems: "center",
                mx: "auto",
                mb: 2,
                fontSize: 30,
                fontWeight: 700,
              }}
            >
              ✓
            </Box>

            <Typography variant="h5" fontWeight={700}>
              Purchase submitted!
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, lineHeight: 1.8, maxWidth: 520, mx: "auto" }}
            >
              Your purchase has been submitted for validation. You will be
              notified via email about the outcome.
            </Typography>

            {purchaseReference && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: "#f5f6f8",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  SUBMISSION REFERENCE
                </Typography>

                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ mt: 0.5, overflowWrap: "anywhere" }}
                >
                  {purchaseReference}
                </Typography>
              </Box>
            )}

            <Button
              variant="contained"
              onClick={() => navigate(`/tm/loyalty/promotions/${ref}`)}
              sx={{
                mt: 3,
                minHeight: 46,
                px: 3,
                borderRadius: 1.5,
                bgcolor: "#202329",
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { bgcolor: "#363a43", boxShadow: "none" },
              }}
            >
              Back to promotion
            </Button>
          </Card>
        ) : (
          <Stack spacing={2.5}>
            <Card sx={{ ...cardSx, p: { xs: 2, md: 2.75 } }}>
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    flexShrink: 0,
                    borderRadius: 1.5,
                    bgcolor: "#e7f5ec",
                    color: "#267446",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 21,
                    fontWeight: 700,
                  }}
                >
                  <Icon>info</Icon>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Before you submit
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, lineHeight: 1.8 }}
                  >
                    Ensure you have clear photos of the selected product
                    purchased and a valid purchase receipt or any other purchase
                    evidence.
                  </Typography>
                </Box>
              </Stack>
            </Card>

            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 1.4, fontWeight: 700 }}
              >
                STEP 1
              </Typography>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ mt: 0.25, mb: 0.75, letterSpacing: -0.4 }}
              >
                Enter your loyalty code
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Provide the loyalty code you received when you registered for
                this promotion.
              </Typography>
            </Box>

            <Card sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
              {/* <Typography variant="subtitle1" fontWeight={700}>
                Your loyalty code
              </Typography> */}

              {/* <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, mb: 2, lineHeight: 1.7 }}
              >
                Enter your loyalty code you received when you registered for
                this promotion.
              </Typography> */}

              <Box mt={0.5}>
                <TextField
                  label="Loyalty code"
                  value={token}
                  onChange={(event) => setToken(event.target.value)}
                  placeholder="Enter your loyalty code"
                  fullWidth
                  required
                  autoComplete="off"
                />
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "block", mt: 0 }}
                align="center"
              >
                Haven't registered yet? &nbsp;
                <Typography
                  component="button"
                  type="button"
                  onClick={() => {
                    resetRegistration();
                    setRegistrationOpen(true);
                  }}
                  mt={2}
                  sx={{
                    border: 0,
                    p: 0,
                    bgcolor: "transparent",
                    color: "#626773",
                    fontSize: 15,
                    textAlign: "center",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    "&:hover": {
                      color: "#202329",
                    },
                  }}
                  fontWeight={800}
                >
                  Register Now
                </Typography>
              </Typography>
            </Card>

            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 1.4, fontWeight: 700 }}
              >
                STEP 2
              </Typography>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ mt: 0.25, mb: 0.75, letterSpacing: -0.4 }}
              >
                Select the product
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Choose the eligible product shown on your purchase evidence.
              </Typography>
            </Box>

            {products.length === 0 ? (
              <Card sx={{ ...cardSx, p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  There are currently no eligible products available for this
                  promotion.
                </Typography>
              </Card>
            ) : (
              <Stack spacing={1}>
                {products.map((product) => {
                  const selected = selectedProduct === product.reference;

                  return (
                    <Card
                      key={product.reference}
                      onClick={() => setSelectedProduct(product.reference)}
                      sx={{
                        ...cardSx,
                        p: 1.5,
                        cursor: "pointer",
                        borderColor: selected ? "#202329" : "divider",
                        bgcolor: selected ? "#f0f1f3" : "#fff",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        {product.image ? (
                          <Box
                            component="img"
                            src={getImageUrl(product.image)}
                            alt={product.title}
                            sx={{
                              width: 76,
                              height: 76,
                              flexShrink: 0,
                              objectFit: "contain",
                              borderRadius: 1.5,
                              bgcolor: "#fff",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 76,
                              height: 76,
                              flexShrink: 0,
                              display: "grid",
                              placeItems: "center",
                              borderRadius: 1.5,
                              bgcolor: "#f0f1f3",
                              color: "#777c85",
                              fontSize: 12,
                              textAlign: "center",
                            }}
                          >
                            No image
                          </Box>
                        )}

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{ overflowWrap: "anywhere" }}
                          >
                            {product.title}
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            {selected ? "Selected" : "Tap to select"}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            border: "2px solid",
                            borderColor: selected ? "#202329" : "#b6bac2",
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                          }}
                        >
                          {selected && (
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#202329",
                              }}
                            />
                          )}
                        </Box>
                      </Stack>
                    </Card>
                  );
                })}
              </Stack>
            )}

            <Divider />

            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 1.4, fontWeight: 700 }}
              >
                STEP 3
              </Typography>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ mt: 0.25, mb: 0.75, letterSpacing: -0.4 }}
              >
                Upload your evidence
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Both uploads are required to submit your claim.
              </Typography>
            </Box>

            <Card sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Product photo
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, mb: 1.5, lineHeight: 1.7 }}
                  >
                    Upload a clear photo of the product you purchased.
                  </Typography>

                  {productImagePreview ? (
                    <Box
                      sx={{
                        ...uploadBoxSx,
                        bgcolor: "#fff",
                        p: 1.5,
                      }}
                    >
                      <Box
                        component="img"
                        src={productImagePreview}
                        alt="Product preview"
                        sx={{
                          width: "100%",
                          maxHeight: 260,
                          objectFit: "contain",
                          borderRadius: 1,
                        }}
                      />

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, overflowWrap: "anywhere" }}
                      >
                        {productImage?.name}
                      </Typography>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{ mt: 1.5, width: "100%" }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Icon>photo_camera</Icon>}
                          onClick={() => openCamera("product")}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            color: "#202329",
                            borderColor: "#d2d5da",
                          }}
                        >
                          Take photo
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Icon>upload_file</Icon>}
                          onClick={() => productImageInput.current?.click()}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            color: "#202329",
                            borderColor: "#d2d5da",
                          }}
                        >
                          Browse files
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    <Box sx={uploadBoxSx}>
                      <Icon sx={{ fontSize: 30, color: "#626773", mb: 0.5 }}>
                        photo_camera
                      </Icon>

                      <Typography variant="body2" fontWeight={600}>
                        Product photo
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Take a photo or select an image
                      </Typography>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{ mt: 1.5, width: "100%" }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Icon>photo_camera</Icon>}
                          onClick={() => openCamera("product")}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            bgcolor: "#202329",
                            boxShadow: "none",
                            "&:hover": {
                              bgcolor: "#363a43",
                              boxShadow: "none",
                            },
                          }}
                        >
                          Take photo
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Icon>upload_file</Icon>}
                          onClick={() => productImageInput.current?.click()}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            color: "#202329",
                            borderColor: "#d2d5da",
                          }}
                        >
                          Browse files
                        </Button>
                      </Stack>
                    </Box>
                  )}

                  <input
                    ref={productImageInput}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) => {
                      handleProductImage(event.target.files?.[0]);
                      event.target.value = "";
                    }}
                  />
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Purchase evidence
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, mb: 1.5, lineHeight: 1.7 }}
                  >
                    Upload a clear image or PDF of your receipt or other
                    purchase evidence.
                  </Typography>

                  {purchaseEvidence ? (
                    <Box sx={uploadBoxSx}>
                      {purchaseEvidencePreview ? (
                        <Box
                          component="img"
                          src={purchaseEvidencePreview}
                          alt="Purchase evidence preview"
                          sx={{
                            width: "100%",
                            maxHeight: 260,
                            objectFit: "contain",
                            borderRadius: 1,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 1.5,
                            bgcolor: "#e9eaed",
                            display: "grid",
                            placeItems: "center",
                            fontWeight: 800,
                            color: "#343840",
                            mb: 1,
                          }}
                        >
                          PDF
                        </Box>
                      )}

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, overflowWrap: "anywhere" }}
                      >
                        {purchaseEvidence.name}
                      </Typography>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{ mt: 1.5, width: "100%" }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Icon>photo_camera</Icon>}
                          onClick={() => openCamera("evidence")}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            color: "#202329",
                            borderColor: "#d2d5da",
                          }}
                        >
                          Take photo
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Icon>upload_file</Icon>}
                          onClick={() => purchaseEvidenceInput.current?.click()}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            color: "#202329",
                            borderColor: "#d2d5da",
                          }}
                        >
                          Browse files
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    <Box sx={uploadBoxSx}>
                      <Icon sx={{ fontSize: 30, color: "#626773", mb: 0.5 }}>
                        receipt_long
                      </Icon>

                      <Typography variant="body2" fontWeight={600}>
                        Purchase evidence
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Take a receipt photo or select an image or PDF
                      </Typography>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{ mt: 1.5, width: "100%" }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Icon>photo_camera</Icon>}
                          onClick={() => openCamera("evidence")}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            bgcolor: "#202329",
                            boxShadow: "none",
                            "&:hover": {
                              bgcolor: "#363a43",
                              boxShadow: "none",
                            },
                          }}
                        >
                          Take photo
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Icon>upload_file</Icon>}
                          onClick={() => purchaseEvidenceInput.current?.click()}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            color: "#202329",
                            borderColor: "#d2d5da",
                          }}
                        >
                          Browse files
                        </Button>
                      </Stack>
                    </Box>
                  )}

                  <input
                    ref={purchaseEvidenceInput}
                    type="file"
                    accept="image/*,application/pdf"
                    hidden
                    onChange={(event) => {
                      handlePurchaseEvidence(event.target.files?.[0]);
                      event.target.value = "";
                    }}
                  />
                </Box>
              </Stack>
            </Card>

            <Card sx={{ ...cardSx, p: { xs: 2.5, md: 3 } }}>
              <Stack spacing={1.5} alignItems="stretch">
                <Button
                  variant="contained"
                  size="large"
                  onClick={submitClaim}
                  disabled={submitting || products.length === 0}
                  fullWidth
                  sx={{
                    minHeight: 50,
                    borderRadius: 1.5,
                    px: 3,
                    bgcolor: "#202329",
                    color: "#fff",
                    fontSize: 15,
                    fontWeight: 700,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#363a43",
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#d7d9dd",
                      color: "#858991",
                    },
                  }}
                >
                  {submitting ? "Submitting..." : "Submit Purchase"}
                </Button>
              </Stack>
            </Card>

            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ px: 2, lineHeight: 1.7 }}
            >
              Your submission will be validated. You will be notified via email
              about the outcome.
            </Typography>
          </Stack>
        )}
      </Box>

      <Dialog
        open={cameraOpen}
        onClose={closeCamera}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {cameraTarget === "product"
            ? "Take product photo"
            : "Take purchase evidence photo"}
        </DialogTitle>

        <DialogContent>
          {cameraError ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {cameraError}
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Position the{" "}
              {cameraTarget === "product"
                ? "product"
                : "receipt or purchase evidence"}{" "}
              in the camera view, then take the photo.
            </Typography>
          )}

          <Box
            sx={{
              width: "100%",
              bgcolor: "#111",
              borderRadius: 1.5,
              overflow: "hidden",
              minHeight: 220,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="video"
              ref={cameraVideoRef}
              autoPlay
              muted
              playsInline
              sx={{
                display: cameraError ? "none" : "block",
                width: "100%",
                maxHeight: "65vh",
                objectFit: "contain",
              }}
            />

            {cameraError && (
              <Typography
                color="#fff"
                variant="body2"
                sx={{ p: 3, textAlign: "center" }}
              >
                Camera preview unavailable
              </Typography>
            )}
          </Box>

          <canvas ref={cameraCanvasRef} style={{ display: "none" }} />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={closeCamera}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={captureCameraPhoto}
            disabled={Boolean(cameraError)}
            startIcon={<Icon>camera</Icon>}
            sx={{
              bgcolor: "#202329",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#363a43",
                boxShadow: "none",
              },
            }}
          >
            Capture photo
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={registrationOpen}
        onClose={() => !registering && setRegistrationOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 2, p: 0.5 },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, fontWeight: 700 }}>
          Register to participate
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, lineHeight: 1.7 }}
          >
            Enter your details to register for this loyalty promotion. Your
            unique code will be added to the claim form after registration.
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="First name"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              autoComplete="given-name"
              required
              fullWidth
            />

            <TextField
              label="Last name"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              autoComplete="family-name"
              required
              fullWidth
            />

            <TextField
              label="Email address"
              type="email"
              value={emailAddress}
              onChange={(event) => setEmailAddress(event.target.value)}
              autoComplete="email"
              required
              fullWidth
            />

            <TextField
              label="Phone number"
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(event.target.value)}
              autoComplete="tel"
              required
              fullWidth
            />

            <FormControl>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                Gender
              </Typography>

              <RadioGroup
                row
                value={gender}
                onChange={(event) => setGender(event.target.value)}
              >
                <FormControlLabel
                  value="MALE"
                  control={<Radio size="small" />}
                  label="Male"
                />

                <FormControlLabel
                  value="FEMALE"
                  control={<Radio size="small" />}
                  label="Female"
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setRegistrationOpen(false)}
            disabled={registering}
            sx={{ color: "text.secondary", textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={registerConsumer}
            disabled={registering}
            sx={{
              bgcolor: "#202329",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#363a43",
                boxShadow: "none",
              },
            }}
          >
            {registering ? "Registering..." : "Register"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LoyaltyPromotionClaimPage;
